import { createTheme, alpha } from "@mui/material/styles";

// The suite's colours (same slate + green as sFass)
export const brand = {
  ink: "#0F1B24",
  slate: "#3D4F58",
  slateDark: "#21313C",
  green: "#17B859",
  greenDark: "#118A43",
  teal: "#50BFB6",
  bg: "#F4F6F8",
  border: "#E3E8EC",
  muted: "#64748B",
};

const theme = createTheme({
  palette: {
    primary: { main: brand.slate, dark: brand.slateDark, contrastText: "#fff" },
    secondary: { main: brand.green, dark: brand.greenDark, contrastText: "#fff" },
    success: { main: "#15803D" },
    warning: { main: "#B45309" },
    error: { main: "#B91C1C" },
    background: { default: brand.bg, paper: "#FFFFFF" },
    text: { primary: brand.ink, secondary: brand.muted },
    divider: brand.border,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 13,
    h4: { fontWeight: 700, fontSize: "1.6rem", letterSpacing: "-0.01em" },
    h5: { fontWeight: 700, fontSize: "1.3rem", letterSpacing: "-0.01em" },
    h6: { fontWeight: 650, fontSize: "1.05rem" },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: "0.82rem" },
    button: { textTransform: "none", fontWeight: 600 },
    overline: { fontWeight: 700, letterSpacing: "0.08em", lineHeight: 1.6 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: brand.bg },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-thumb": {
          background: "#C3CBD2",
          borderRadius: 8,
        },
        "*::-webkit-scrollbar-track": { background: "transparent" },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 8, paddingInline: 14 } },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        outlined: { borderColor: brand.border },
        rounded: { borderRadius: 12 },
      },
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: { root: { borderRadius: 12, borderColor: brand.border } },
    },
    MuiTextField: { defaultProps: { size: "small" } },
    MuiFormControl: { defaultProps: { size: "small" } },
    MuiOutlinedInput: {
      styleOverrides: { root: { backgroundColor: "#fff", borderRadius: 8 } },
    },
    MuiTooltip: { defaultProps: { arrow: true } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 14 } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: "1.1rem" } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#F8FAFB",
          color: brand.muted,
          fontWeight: 700,
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        },
        root: { borderColor: brand.border },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, minHeight: 44 },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${brand.border}`,
          borderRadius: 12,
          backgroundColor: "#fff",
          fontSize: "0.8rem",
          "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8FAFB" },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            color: brand.muted,
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(brand.teal, 0.06),
          },
          "& .MuiDataGrid-toolbarContainer": {
            padding: "8px 10px",
            borderBottom: `1px solid ${brand.border}`,
            gap: 8,
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
        },
      },
    },
  },
});

export default theme;
